export const UNIT_STORAGE_KEY = 'asijolab.unitSystem';
export const MM_PER_INCH = 25.4;
export const UM_PER_INCH = 25400;
export const MPA_PER_KSI = 6.894757293168361;
export const FEET_PER_METRE = 3.280839895013123;

const definitions = Object.freeze({
  mm: { metric: 'mm', inch: 'in', toInch: value => value / MM_PER_INCH, toMetric: value => value * MM_PER_INCH },
  um: { metric: 'µm', inch: 'in', toInch: value => value / UM_PER_INCH, toMetric: value => value * UM_PER_INCH },
  mpa: { metric: 'MPa', inch: 'ksi', toInch: value => value / MPA_PER_KSI, toMetric: value => value * MPA_PER_KSI },
  cuttingSpeed: { metric: 'm/min', inch: 'SFM', toInch: value => value * FEET_PER_METRE, toMetric: value => value / FEET_PER_METRE },
  feedRate: { metric: 'mm/min', inch: 'IPM', toInch: value => value / MM_PER_INCH, toMetric: value => value * MM_PER_INCH },
  feedPerTooth: { metric: 'mm/tooth', inch: 'IPT', toInch: value => value / MM_PER_INCH, toMetric: value => value * MM_PER_INCH },
  feedPerRevolution: { metric: 'mm/rev', inch: 'in/rev', toInch: value => value / MM_PER_INCH, toMetric: value => value * MM_PER_INCH },
});

export const unitLabel = (kind, system) => definitions[kind][system];
export const toDisplay = (value, kind, system) => system === 'inch' ? definitions[kind].toInch(Number(value)) : Number(value);
export const toCanonical = (value, kind, system) => system === 'inch' ? definitions[kind].toMetric(Number(value)) : Number(value);

const fixed = (value, decimals) => Number(value).toFixed(decimals);
const trim = value => value.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');

export function formatUnitValue(value, kind, system, { input = false, signed = false, metricDecimals } = {}) {
  if (value === '' || value === null || value === undefined) return '';
  const number = toDisplay(value, kind, system);
  if (!Number.isFinite(number)) return '';
  let result;
  if (system === 'metric') {
    result = metricDecimals === undefined ? String(Number(number.toPrecision(14))) : fixed(number, metricDecimals);
  } else if (kind === 'um') {
    result = fixed(number, input ? 12 : Math.abs(number) < 0.00001 ? 8 : 6);
  } else if (kind === 'mm') {
    result = fixed(number, input ? 12 : Math.abs(number) < 0.0001 ? 8 : Math.abs(number) < 0.01 ? 6 : 5);
  } else if (kind === 'feedPerTooth' || kind === 'feedPerRevolution') {
    result = fixed(number, input ? 12 : 5);
  } else if (kind === 'feedRate') {
    result = fixed(number, input ? 8 : 2);
  } else if (kind === 'cuttingSpeed') {
    result = fixed(number, input ? 8 : 0);
  } else {
    result = fixed(number, input ? 12 : 1);
  }
  if (input || metricDecimals === undefined) result = trim(result);
  if (signed && Number(result) > 0) return `+${result}`;
  return result;
}

export function displayUnitValue(value, kind, system, options) {
  return `${formatUnitValue(value, kind, system, options)} ${unitLabel(kind, system)}`;
}

export function displayUnitRange(minimum, maximum, kind, system, { separator = 'to', ...formatOptions } = {}) {
  return `${formatUnitValue(minimum, kind, system, formatOptions)} ${separator} ${formatUnitValue(maximum, kind, system, formatOptions)} ${unitLabel(kind, system)}`;
}

export const THREAD_DIMENSION_PRECISION = Object.freeze({ metric: 3, inch: 4 });

export function formatDualDimension(valueMm, preferredSystem, {
  metricDecimals = THREAD_DIMENSION_PRECISION.metric,
  inchDecimals = THREAD_DIMENSION_PRECISION.inch,
} = {}) {
  const canonical = Number(valueMm);
  if (!Number.isFinite(canonical)) return { primary: '', secondary: '' };
  const metric = `${fixed(canonical, metricDecimals)} mm`;
  const inch = `${fixed(toDisplay(canonical, 'mm', 'inch'), inchDecimals)} in`;
  return preferredSystem === 'inch'
    ? { primary: inch, secondary: metric }
    : { primary: metric, secondary: inch };
}

export const machiningKind = Object.freeze({ diameter: 'mm', ap: 'mm', ae: 'mm', vc: 'cuttingSpeed', fz: 'feedPerTooth', feedRate: 'feedRate' });
